/**
 * Валидация возврата по исходному чеку (ARCH-TASK-65.5).
 *
 * Чистая логика без Dexie: лимит считается накопительно по всем возвратам,
 * уже связанным с этим чеком. Ключ позиции — штрихкод, а при его отсутствии
 * «название + цена» (в чеке бывают товары без ШК, заведённые «на лету»).
 *
 * Допуск 1e-3 — весовые товары: 0.300 кг не должно «не влезать» из-за
 * плавающей точки (0.1 + 0.2 ≠ 0.3 в double).
 */

/** Минимальная позиция чека, нужная для сверки. */
export interface ReturnableItem {
  /**
   * id строки (65.10). У исходного чека — его lineId, у возврата —
   * originalLineId (какую строку исходного чека возвращаем).
   */
  lineId?: string
  /** Для старых чеков без lineId: код маркировки однозначно адресует строку. */
  datamatrix?: string
  barcode: string
  name: string
  price: number
  qty: number
}

/** Допуск сравнения количеств (весовые товары). */
const QTY_EPSILON = 1e-3

/**
 * Ключ позиции.
 *
 * Приоритет (65.10): lineId → DataMatrix → штрихкод → «название|цена».
 * Для маркированного товара это даёт line-level возврат: одна строка = один код,
 * а не «сколько-то единиц по штрихкоду».
 */
export function returnItemKey(item: {
  lineId?: string
  datamatrix?: string
  barcode?: string
  name: string
  price: number
}): string {
  if (item.lineId?.trim()) return `line:${item.lineId.trim()}`
  if (item.datamatrix?.trim()) return `dm:${item.datamatrix.trim()}`
  return item.barcode?.trim() ? item.barcode.trim() : `${item.name.trim()}|${item.price}`
}

/** Сумма количеств по ключам позиций. */
function sumByKey(items: ReturnableItem[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const item of items) {
    const key = returnItemKey(item)
    map.set(key, (map.get(key) ?? 0) + item.qty)
  }
  return map
}

export type ReturnValidationResult = { ok: true } | { ok: false; reason: string }

/**
 * Проверить, что возврат не превышает проданное минус уже возвращённое.
 *
 * @param sold позиции исходного чека.
 * @param alreadyReturned позиции всех ранее оформленных возвратов по нему.
 * @param requested позиции текущего возврата.
 * @param subject что проверяем (для текста ошибки): «возврате» / «коррекции».
 */
function validateItems(
  sold: ReturnableItem[],
  alreadyReturned: ReturnableItem[],
  requested: ReturnableItem[],
  subject: 'возврате' | 'коррекции'
): ReturnValidationResult {
  if (requested.length === 0) {
    return { ok: false, reason: subject === 'коррекции' ? 'Состав коррекции пуст' : 'Возврат пуст' }
  }

  const soldByKey = sumByKey(sold)
  const returnedByKey = sumByKey(alreadyReturned)
  const requestedByKey = sumByKey(requested)

  for (const [key, qty] of requestedByKey) {
    const soldQty = soldByKey.get(key)
    if (soldQty == null) {
      return {
        ok: false,
        reason: `В ${subject} есть позиция, которой не было в исходном чеке`
      }
    }
    const available = soldQty - (returnedByKey.get(key) ?? 0)
    if (available <= QTY_EPSILON) {
      return {
        ok: false,
        reason: 'По этой позиции уже возвращено всё проданное количество'
      }
    }
    if (qty > available + QTY_EPSILON) {
      return {
        ok: false,
        reason: `Нельзя вернуть больше проданного: доступно ${Number(available.toFixed(3))}`
      }
    }
  }

  return { ok: true }
}

export function validateReturnItems(
  sold: ReturnableItem[],
  alreadyReturned: ReturnableItem[],
  requested: ReturnableItem[]
): ReturnValidationResult {
  return validateItems(sold, alreadyReturned, requested, 'возврате')
}

/**
 * Проверка состава чека коррекции возврата прихода (ARCH-TASK-65.6):
 * те же правила, что у обычного возврата, но с текстами про коррекцию.
 */
export function validateCorrectionItems(
  sold: ReturnableItem[],
  alreadyReturned: ReturnableItem[],
  requested: ReturnableItem[]
): ReturnValidationResult {
  return validateItems(sold, alreadyReturned, requested, 'коррекции')
}

/**
 * Лимит СУММЫ для чеков коррекции возврата прихода (ARCH-TASK-65.6).
 *
 * Коррекция суммовая (позиций нет), поэтому контроль — по деньгам: сумма всех
 * коррекций-возвратов по чеку плюс текущая не может превысить сумму продажи.
 */
export function validateCorrectionTotal(
  sourceTotal: number,
  alreadyReturnedTotal: number,
  requested: number
): ReturnValidationResult {
  if (!Number.isFinite(requested) || requested <= 0) {
    return { ok: false, reason: 'Сумма коррекции должна быть больше нуля' }
  }
  const available = Math.abs(sourceTotal) - Math.abs(alreadyReturnedTotal)
  if (available <= 0.01) {
    return { ok: false, reason: 'По этому чеку уже возвращена вся сумма' }
  }
  if (requested > available + 0.01) {
    return {
      ok: false,
      reason: `Нельзя вернуть больше суммы продажи: доступно ${Number(available.toFixed(2))}`
    }
  }
  return { ok: true }
}

/**
 * Сколько ещё можно вернуть по КАЖДОЙ СТРОКЕ чека (для UI: maxQty строки).
 *
 * Остаток по ключу позиции распределяется по строкам последовательно: если в
 * чеке две строки одного товара (маркированный: один ШК, разные DataMatrix),
 * нельзя давать полный остаток каждой — иначе в корзину попадёт вдвое больше
 * проданного (ревью 65.5).
 *
 * @returns массив остатков той же длины и порядка, что `sold` (может быть 0).
 */
export function returnableQtyByLine(
  sold: ReturnableItem[],
  alreadyReturned: ReturnableItem[]
): number[] {
  const leftByKey = sumByKey(sold)
  for (const [key, qty] of sumByKey(alreadyReturned)) {
    leftByKey.set(key, Math.max(0, (leftByKey.get(key) ?? 0) - qty))
  }

  return sold.map((item) => {
    const key = returnItemKey(item)
    const left = leftByKey.get(key) ?? 0
    const take = Math.min(item.qty, left)
    const rounded = take > QTY_EPSILON ? Number(take.toFixed(3)) : 0
    // Списываем выданное, чтобы следующая строка того же ключа получила меньше.
    leftByKey.set(key, Math.max(0, Number((left - rounded).toFixed(3))))
    return rounded
  })
}

